import { API_BASE } from './base'
import type {
    StockFinancialData,
    StockFinancialResponse,
    StockBaseMetricsResponse,
    StockKlinePoint,
    StockProfileResponse,
    StockGovernanceResponse,
    StockQuoteResponse,
    StockRevenueBreakdownResponse,
    StockPlateStocksResponse,
    StockReport,
    StockReportDetail,
} from '@/types/stockPool'

// 个股知识库列表项
export interface StockKBItem {
    stock_code: string
    name: string | null
    fiscal_year: string | null
    report_type: string | null
    report_date: string | null
    closing_price: number | null
    market_cap: number | null
    pe_ttm: number | null
    pb_ratio: number | null
    revenue_yoy_pct: number | null
    adjusted_net_profit_yoy_pct: number | null
    currency: string | null
    market: 'hk' | 'us' | 'a' | 'other'
}

export interface StockKBListResponse {
    total: number
    page: number
    page_size: number
    items: StockKBItem[]
}

export interface StockKBStats {
    total_stocks: number
    total_reports: number
    market_breakdown: Record<string, number>
    report_type_breakdown: Record<string, number>
    recent_reports: Array<{
        id: number
        stock_code: string
        name: string | null
        fiscal_year: string | null
        report_type: string | null
        report_date: string | null
        currency: string | null
    }>
}

// 个股知识库列表（新增接口，支持分页）
export async function getStockKBList(params: {
    q?: string; market?: string; page?: number; page_size?: number
} = {}): Promise<StockKBListResponse> {
    const qs = new URLSearchParams()
    if (params.q) qs.set('q', params.q)
    if (params.market && params.market !== 'all') qs.set('market', params.market)
    if (params.page != null) qs.set('page', String(params.page))
    if (params.page_size != null) qs.set('page_size', String(params.page_size))
    const res = await fetch(`${API_BASE}/stocks/kb-list?${qs}`)
    if (!res.ok) throw new Error(`kb-list failed: ${res.status}`)
    return res.json()
}

// 个股知识库统计面板（新增接口）
export async function getStockKBStats(): Promise<StockKBStats> {
    const res = await fetch(`${API_BASE}/stocks/kb-stats`)
    if (!res.ok) throw new Error(`kb-stats failed: ${res.status}`)
    return res.json()
}

//股票财务数据
export async function getStockFinancedetail(code: string): Promise<StockFinancialResponse>{
    const res = await fetch(`${API_BASE}/stocks/financials?code=${code}`)
    return res.json()
}

//股票主体基础指标
export async function getStockBaseMetrics(code: string): Promise<StockBaseMetricsResponse | null> {
    const res = await fetch(`${API_BASE}/stocks/base-metrics?code=${code}`)
    if (!res.ok) return null
    return res.json()
}

//股票 K 线图数据
export async function getStockKlines(code: string, days = 30): Promise<StockKlinePoint[]> {
    const res = await fetch(`${API_BASE}/stocks/klines?code=${code}&days=${days}`)
    const data = await res.json()
    return data.items ?? []
}

//股票公司概况 + 板块分类
export async function getStockProfile(code: string): Promise<StockProfileResponse | null> {
    const res = await fetch(`${API_BASE}/stocks/profile?code=${code}`)
    if (!res.ok) return null
    return res.json()
}

//股票主营业务营收构成
export async function getStockRevenueBreakdown(
    code: string,
    period = '',
    dimension?: 1 | 2 | 4 | 8,
): Promise<StockRevenueBreakdownResponse | null> {
    const qs = new URLSearchParams({ code })
    if (period) qs.set('period', period)
    if (dimension != null) qs.set('dimension', String(dimension))
    const res = await fetch(`${API_BASE}/stocks/revenue-breakdown?${qs}`)
    if (!res.ok) return null
    return res.json()
}

//板块成份股市值与估值
export async function getStockPlateStocks(
    plate: string,
    options: {
        limit?: number
        valuation_type?: 1 | 2 | 3
        sort_id?: 'market_cap' | 'valuation' | 'historical_percentile'
        sort_type?: 'asc' | 'desc'
    } = {},
): Promise<StockPlateStocksResponse | null> {
    const qs = new URLSearchParams({
        plate,
        valuation_type: String(options.valuation_type ?? 1),
        sort_id: options.sort_id ?? 'market_cap',
        sort_type: options.sort_type ?? 'desc',
        limit: String(options.limit ?? 10),
    })
    const res = await fetch(`${API_BASE}/stocks/plate-stocks?${qs}`)
    if (!res.ok) return null
    return res.json()
}

//股票治理结构（管理层 + 前十大股东）
export async function getStockGovernance(code: string): Promise<StockGovernanceResponse | null> {
    const res = await fetch(`${API_BASE}/stocks/governance?code=${code}`)
    if (!res.ok) return null
    return res.json()
}

//股票实时行情快照
export async function getStockQuote(code: string): Promise<StockQuoteResponse | null> {
    const res = await fetch(`${API_BASE}/stocks/quote?code=${code}`)
    if (!res.ok) return null
    return res.json()
}

export interface StockReportsResponse {
    code: string
    total: number
    items: StockReport[]
}

//股票研报（report_type 逗号分隔：annual,interim_h1,research 等）
export async function getStockReports(params: {
    code: string
    report_type?: string
    limit?: number
}): Promise<StockReportsResponse> {
    const qs = new URLSearchParams({ code: params.code })
    if (params.report_type) qs.set('report_type', params.report_type)
    if (params.limit != null) qs.set('limit', String(params.limit))
    const res = await fetch(`${API_BASE}/stocks/reports?${qs}`)
    if (!res.ok) throw new Error(`reports failed: ${res.status}`)
    return res.json()
}

//股票研报详情
export async function getStockReportDetail({id}: {id: number}): Promise<StockReportDetail> {
    const res = await fetch(`${API_BASE}/stocks/reports/${id}`)
    if (!res.ok) throw new Error(`report detail failed: ${res.status}`)
    return res.json()
}

//股票问答
export async function getStockQuestions({
    code,
    question,
    skillId,
    sourceFileId,
    onSectionComplete,
    onSectionStart,
    onSectionDelta,
}: {
    code: string
    question: string
    skillId?: string
    sourceFileId?: number
    onSectionComplete?: (section: { key: string, title: string, markdown: string }) => void
    onSectionStart?: (section: { key: string, title: string }) => void
    onSectionDelta?: (delta: { key: string, text: string }) => void
}) {
    const res = await fetch(`${API_BASE}/stocks/${code}/reports/rag-ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            question,
            skill_id: skillId,
            source_file_id: sourceFileId,
        }),
    })
    if (!res.ok || !res.body) throw new Error(`rag-ask failed: ${res.status}`)

    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    while (true) {
        const { done, value } = await reader.read()
        buffer += decoder.decode(value ?? new Uint8Array(), { stream: !done })
        let boundary = buffer.indexOf('\n\n')
        while (boundary >= 0) {
            const frame = buffer.slice(0, boundary)
            buffer = buffer.slice(boundary + 2)
            boundary = buffer.indexOf('\n\n')
            if (!frame || frame.startsWith(':')) continue
            const event = frame.match(/^event:\s*(.+)$/m)?.[1] ?? 'message'
            const rawData = frame.match(/^data:\s*(.+)$/m)?.[1]
            if (!rawData) continue
            const data = JSON.parse(rawData)
            if (event === 'result') return data
            if (event === 'section_start') onSectionStart?.(data)
            if (event === 'section_delta') onSectionDelta?.(data)
            if (event === 'section_complete') onSectionComplete?.(data)
            if (event === 'error') throw new Error(data.detail || 'rag-ask failed')
        }
        if (done) break
    }
    throw new Error('rag-ask stream ended before a result was returned')
}

//季度报表财务数据分页响应
export interface StockQuarterlyFinancialResponse {
    code: string
    name: string | null
    page: number
    page_size: number
    total: number
    items: StockFinancialData[]
}

//股票季度报表财务数据（quarterly_report，1-4 季度，按报告日期倒序分页）
export async function getStockQuarterlyFinancials(params: {
    code: string
    page?: number
    page_size?: number
}): Promise<StockQuarterlyFinancialResponse> {
    const qs = new URLSearchParams({ code: params.code })
    if (params.page != null) qs.set('page', String(params.page))
    if (params.page_size != null) qs.set('page_size', String(params.page_size))
    const res = await fetch(`${API_BASE}/stocks/quarterly-financials?${qs}`)
    if (!res.ok) throw new Error(`quarterly-financials failed: ${res.status}`)
    return res.json()
}

//单条季度报表财务数据详情（全部字段）
export interface StockQuarterlyFinancialDetailResponse {
    id: number
    item: StockFinancialData
}

export async function getStockQuarterlyFinancialDetail(reportId: number): Promise<StockQuarterlyFinancialDetailResponse> {
    const res = await fetch(`${API_BASE}/stocks/quarterly-financials/${reportId}`)
    if (!res.ok) throw new Error(`quarterly-financials detail failed: ${res.status}`)
    return res.json()
}

// ── ops 财报原始数据（回测/模拟盘 AI 调仓的数据源） ─────────────────────────

/** ops 财报历史单期原始行（字段名随市场/上游口径而异，用宽类型承载） */
export interface OpsFinancialRow {
    [key: string]: unknown
}

export interface StockOpsFinancialsResponse {
    code: string
    market: string
    as_of: string | null
    total: number
    items: OpsFinancialRow[]
}

/** 查询指定个股在 ops 财报接口的历史财报原始数据（支持 as_of 点-in-time，按报告期倒序） */
export async function getStockOpsFinancials(
    code: string,
    limit = 12,
    asOf?: string,
): Promise<StockOpsFinancialsResponse> {
    const qs = new URLSearchParams({ code, limit: String(limit) })
    if (asOf) qs.set('as_of', asOf)
    const res = await fetch(`${API_BASE}/stocks/ops-financials?${qs}`)
    if (!res.ok) throw new Error(`ops-financials failed: ${res.status}`)
    return res.json()
}

// ── 新知识库 P1 接口 ────────────────────────────────────────────

export interface DividendItem {
    ex_dividend_date: string | null
    record_date: string | null
    pay_date: string | null
    fiscal_year: string | null
    dividend_per_share: number | null
    currency: string | null
    dividend_type: string | null
    payout_ratio: number | null
    statement: string | null
    pub_date: string | null
    process: string | null
}

export interface ValuationHistoricalItem {
    time_str: string
    pe_ttm: number | null
    pb_mrq: number | null
    ps_ttm: number | null
    plate_value: number | null
}

export interface ValuationTrend {
    current_value: number | null
    average_value: number | null
    avg_minus_1_stddev: number | null
    avg_plus_1_stddev: number | null
    valuation_percentile: number | null
    historical_items: ValuationHistoricalItem[]
}

export interface ValuationDetail {
    ticker: string
    valuation_type?: number | null
    trend?: ValuationTrend | null
}

export interface AnalystConsensus {
    snapshot_date: string | null
    rating: number | null
    total_analysts: number | null
    strong_buy_pct: number | null
    buy_pct: number | null
    hold_pct: number | null
    underperform_pct: number | null
    sell_pct: number | null
    target_price_avg: number | null
    target_price_high: number | null
    target_price_low: number | null
    num_target_analysts: number | null
}

// ── 时间线 / 动态监控相关类型（issue #647） ──────────────────────────

/** 公告列表项（notice_detail） */
export interface FilingItem {
    id: number
    ticker: string | null
    filing_type: number | null // 1=earnings 2=trade 3=equity 4=major 5=other
    title: string | null
    abstract: string | null
    filing_source_type: string | null
    published_at: string | null // ISO 8601 UTC
}

/** 公告列表分页响应 */
export interface StockFilingsResponse {
    code: string
    data: FilingItem[]
    total: number
    has_more: boolean
}

/** 新闻列表项（flash_detail） */
export interface NewsItem {
    id: number
    ticker: string | null
    title: string | null
    abstract: string | null
    source: string | null
    url: string | null
    published_at: string | null
}

/** 新闻列表分页响应 */
export interface StockNewsResponse {
    code: string
    data: NewsItem[]
    total: number
    has_more: boolean
}

/** 公告附件单条 */
export interface FilingFileURL {
    filing_file_id: number | null // notice_file.id；未关联时为 null
    file_name: string | null // 原始文件名；未同步到 S3 时为 null
    source_url: string // 原始来源 URL
    download_url: string | null // 下载端点路径；未同步到 S3 时为 null
}

/** 公告附件列表响应 */
export interface FilingFilesResponse {
    id: number
    filing_file_urls: FilingFileURL[]
}

/** 除权复权（fd_rehab） */
export interface CorporateActionItem {
    ticker: string
    ex_div_date: string | null
    event_date: null
    split_ratio: number | null
    join_ratio: number | null
    bonus_base: number | null
    bonus_ratio: number | null
    transfer_base: number | null
    transfer_ert: number | null
    allotment_ratio: number | null
    allotment_price: number | null
    dividend: number | null
    special_dividend: number | null
    special_dividend_base: number | null
    add_base: number | null
    add_ert: number | null
    spin_off_ratio: number | null
    forward_factor_a: string
    forward_factor_b: string
    backward_factor_a: string
    backward_factor_b: string
    action_types: string | null // JSON 数组字符串，如 ["DIVIDEND","SPLIT"]
    desc_sc: string | null
    desc_tc: string | null
    desc_en: string | null
}

/** 资金流向（fd_capital_flow） */
export interface CapitalFlowItem {
    ticker: string
    flow_date: string
    in_flow: number | null
    main_in_flow: number | null
    super_in_flow: number | null
    big_in_flow: number | null
    mid_in_flow: number | null
    sml_in_flow: number | null
    main_deal_ratio: number | null
    acc_main_in_flow: number | null
}

/** 每日卖空量（fd_short_volume_daily） */
export interface ShortVolumeItem {
    ticker: string
    trade_date: string
    short_sell_shares: number | null
    total_shares: number | null
    short_ratio: number | null
    turnover: number | null
    short_sell_turnover: number | null
    nasdaq_shares_short: number | null
    nyse_shares_short: number | null
    open_price: number | null
    close_price: number | null
    last_close_price: number | null
    daily_trade_avg_ratio: number | null
    aggregated_short: number | null
    aggregated_short_ratio: number | null
}

/** 空头持仓（fd_short_interest） */
export interface ShortInterestItem {
    ticker: string
    data_date: string
    aggregated_short: number | null
    aggregated_short_ratio: number | null
    avg_daily_short_volume: number | null
    avg_cost: number | null
    avg_daily_share_volume: number | null
    days_to_cover: number | null
    close_price: number | null
    open_price: number | null
    last_close_price: number | null
}

/** 机构持仓趋势（fd_institution_holder） */
export interface InstitutionalHolderItem {
    ticker: string
    period_text: string
    report_date: string | null
    institution_count: number | null
    institution_count_change: number | null
    holder_shares: number | null
    holder_shares_change: number | null
    holder_pct: number | null
    holder_pct_change: number | null
    close_price: number | null
    open_price: number | null
    last_close_price: number | null
}

/** 内部人交易（fd_insider_trades，仅美股） */
export interface InsiderTradeItem {
    ticker: string
    holder_id: number
    holder_name: string | null
    title: string | null
    trade_shares: number | null
    min_trade_date: string
    max_trade_date: string | null
    min_price: number | null
    max_price: number | null
    security_holder_quantity: number | null
    transaction_type: string // BUY / SELL
    source_group_name: string | null
    is_proposed_sale: boolean | null
}

/** 回购历史（fd_buyback） */
export interface BuybackItem {
    ticker: string
    market_type: string // HK / A
    publ_date: string
    end_date: string | null
    shares: number | null
    amount: number | null
    currency: string | null
    percentage: number | null
    high_price: number | null
    low_price: number | null
    cumulative_sum: number | null
    cumulative_percentage: number | null
    share_type: string | null
    start_date: string | null
    event_proce_desc: string | null
    buy_back_mode: string | null
    value_floor: number | null
    value_ceiling: number | null
    price_floor: number | null
    price_ceiling: number | null
}

export async function getStockDividends(code: string, limit = 20): Promise<DividendItem[]> {
    const res = await fetch(`${API_BASE}/stocks/dividends?code=${encodeURIComponent(code)}&limit=${limit}`)
    if (!res.ok) throw new Error(`dividends failed: ${res.status}`)
    const body = await res.json()
    return body.items ?? []
}

/** 时间锚点（YYYY-MM-DD），估值窗口与区间统计以锚点为截止日；不传默认今天 */
export async function getStockValuationHistory(
    code: string,
    intervalType = 3,
    valuationType = 1,
    anchorDate?: string,
): Promise<ValuationDetail | null> {
    const qs = new URLSearchParams({
        code,
        valuation_type: String(valuationType),
        interval_type: String(intervalType),
    })
    if (anchorDate) qs.set('anchor_date', anchorDate)
    const res = await fetch(`${API_BASE}/stocks/valuation-history?${qs.toString()}`)
    if (!res.ok) throw new Error(`valuation-history failed: ${res.status}`)
    const body = await res.json()
    return body.detail ?? null
}

export async function getStockAnalyst(code: string): Promise<AnalystConsensus | null> {
    const res = await fetch(`${API_BASE}/stocks/analyst?code=${encodeURIComponent(code)}`)
    if (!res.ok) throw new Error(`analyst failed: ${res.status}`)
    const body = await res.json()
    return body.consensus ?? null
}

// ── 时间线 / 动态监控 API（issue #647） ──────────────────────────────

/** 公告列表（offset 分页信封透传） */
export async function getStockFilings(
    code: string,
    options: {
        type?: string
        date_from?: string
        date_to?: string
        offset?: number
        limit?: number
    } = {},
): Promise<StockFilingsResponse> {
    const qs = new URLSearchParams({ code })
    if (options.type) qs.set('type', options.type)
    if (options.date_from) qs.set('date_from', options.date_from)
    if (options.date_to) qs.set('date_to', options.date_to)
    qs.set('offset', String(options.offset ?? 0))
    qs.set('limit', String(options.limit ?? 20))
    const res = await fetch(`${API_BASE}/stocks/filings?${qs}`)
    if (!res.ok) throw new Error(`filings failed: ${res.status}`)
    return res.json()
}

/** 新闻列表（offset 分页信封透传） */
export async function getStockNews(
    code: string,
    options: { offset?: number; limit?: number } = {},
): Promise<StockNewsResponse> {
    const qs = new URLSearchParams({ code })
    qs.set('offset', String(options.offset ?? 0))
    qs.set('limit', String(options.limit ?? 20))
    const res = await fetch(`${API_BASE}/stocks/news?${qs}`)
    if (!res.ok) throw new Error(`news failed: ${res.status}`)
    return res.json()
}

/** 除权复权 */
export async function getStockCorporateActions(
    code: string,
    start?: string,
    end?: string,
): Promise<CorporateActionItem[]> {
    const qs = new URLSearchParams({ code })
    if (start) qs.set('start', start)
    if (end) qs.set('end', end)
    const res = await fetch(`${API_BASE}/stocks/corporate-actions?${qs}`)
    if (!res.ok) throw new Error(`corporate-actions failed: ${res.status}`)
    const body = await res.json()
    return body.items ?? []
}

/** 公告附件列表 */
export async function getFilingFiles(filingId: number): Promise<FilingFilesResponse> {
    const res = await fetch(`${API_BASE}/stocks/filings/${filingId}/files`)
    if (!res.ok) throw new Error(`filing files failed: ${res.status}`)
    return res.json()
}

/** 公告附件下载（返回 S3 presigned URL） */
export async function getFilingDownloadUrl(fileId: number): Promise<string> {
    const res = await fetch(`${API_BASE}/stocks/filings/download/${fileId}`, { redirect: 'follow' })
    if (!res.ok) throw new Error(`filing download failed: ${res.status}`)
    // 后端 302 重定向到 S3 presigned URL，fetch follow 后 res.url 即最终 URL
    return res.url
}

/** 新闻正文下载（返回 S3 presigned URL） */
export async function getNewsDownloadUrl(newsId: number): Promise<string> {
    const res = await fetch(`${API_BASE}/stocks/news/${newsId}/download`, { redirect: 'follow' })
    if (!res.ok) throw new Error(`news download failed: ${res.status}`)
    return res.url
}

/** 资金流向 */
export async function getStockCapitalFlow(
    code: string,
    options: { start?: string; end?: string; limit?: number } = {},
): Promise<CapitalFlowItem[]> {
    const qs = new URLSearchParams({ code })
    if (options.start) qs.set('start', options.start)
    if (options.end) qs.set('end', options.end)
    qs.set('limit', String(options.limit ?? 20))
    const res = await fetch(`${API_BASE}/stocks/capital-flow?${qs}`)
    if (!res.ok) throw new Error(`capital-flow failed: ${res.status}`)
    const body = await res.json()
    return body.items ?? []
}

/** 每日卖空量 */
export async function getStockShortVolume(
    code: string,
    options: { start?: string; end?: string; limit?: number } = {},
): Promise<ShortVolumeItem[]> {
    const qs = new URLSearchParams({ code })
    if (options.start) qs.set('start', options.start)
    if (options.end) qs.set('end', options.end)
    qs.set('limit', String(options.limit ?? 30))
    const res = await fetch(`${API_BASE}/stocks/short-volume?${qs}`)
    if (!res.ok) throw new Error(`short-volume failed: ${res.status}`)
    const body = await res.json()
    return body.items ?? []
}

/** 空头持仓 */
export async function getStockShortInterest(
    code: string,
    limit = 12,
): Promise<ShortInterestItem[]> {
    const res = await fetch(`${API_BASE}/stocks/short-interest?code=${encodeURIComponent(code)}&limit=${limit}`)
    if (!res.ok) throw new Error(`short-interest failed: ${res.status}`)
    const body = await res.json()
    return body.items ?? []
}

/** 机构持仓趋势 */
export async function getStockInstitutionalHolders(
    code: string,
    limit = 8,
): Promise<InstitutionalHolderItem[]> {
    const res = await fetch(`${API_BASE}/stocks/institutional-holders?code=${encodeURIComponent(code)}&limit=${limit}`)
    if (!res.ok) throw new Error(`institutional-holders failed: ${res.status}`)
    const body = await res.json()
    return body.items ?? []
}

/** 内部人交易（仅美股有数据） */
export async function getStockInsiderTrades(
    code: string,
    limit = 20,
): Promise<InsiderTradeItem[]> {
    const res = await fetch(`${API_BASE}/stocks/insider-trades?code=${encodeURIComponent(code)}&limit=${limit}`)
    if (!res.ok) throw new Error(`insider-trades failed: ${res.status}`)
    const body = await res.json()
    return body.items ?? []
}

/** 回购历史 */
export async function getStockBuybacks(
    code: string,
    options: { start?: string; end?: string; limit?: number } = {},
): Promise<BuybackItem[]> {
    const qs = new URLSearchParams({ code })
    if (options.start) qs.set('start', options.start)
    if (options.end) qs.set('end', options.end)
    qs.set('limit', String(options.limit ?? 20))
    const res = await fetch(`${API_BASE}/stocks/buybacks?${qs}`)
    if (!res.ok) throw new Error(`buybacks failed: ${res.status}`)
    const body = await res.json()
    return body.items ?? []
}

export interface StockMetaInfo {
    code: string
    name: string
    english_name: string
    sc_name: string
    tc_name: string
    sector: string
    industry: string
    description: string | null
    description_supplement: string | null
    description_period: string | null
}

export async function getStockMeta(code: string): Promise<StockMetaInfo | null> {
    const res = await fetch(`${API_BASE}/stocks/meta?code=${encodeURIComponent(code)}`)
    if (!res.ok) return null
    return res.json()
}

// ── Skill API ────────────────────────────────────────────────────────────────

export interface SkillCacheResult {
    report_id: number
    skill_name: string
    result_md: string
    created_at: string
}

/** 查询已缓存的 skill 结果。无缓存返回 null。 */
export async function getReportSkillResult(
    reportId: number,
    skillName: string,
): Promise<SkillCacheResult | null> {
    const res = await fetch(`${API_BASE}/stocks/reports/${reportId}/skills/${skillName}`)
    if (res.status === 404) return null
    if (!res.ok) throw new Error(`skill cache fetch failed: ${res.status}`)
    return res.json()
}

/**
 * 运行 skill，返回一个 ReadableStreamDefaultReader 供逐行读取 SSE。
 * 调用方负责调用 reader.cancel() 清理资源。
 */
export async function runReportSkill(
    reportId: number,
    skillName: string,
): Promise<ReadableStreamDefaultReader<Uint8Array>> {
    const res = await fetch(`${API_BASE}/stocks/reports/${reportId}/skills/${skillName}/run`, {
        method: 'POST',
    })
    if (!res.ok) throw new Error(`skill run failed: ${res.status}`)
    if (!res.body) throw new Error('response body is null')
    return res.body.getReader()
}

